import {
  IUploadCheckResult,
  IVideoFileUploadInfo,
  IVideoMetaData,
} from "./types";
import { AVCanvas } from "@webav/av-canvas";
import { MP4Clip, VisibleSprite } from "@webav/av-cliper";
import {
  uploadAndCommitCache,
  uploadAndCommitFile,
} from "../file/NovaFileManagerModel";
import { PATH_PREFIX } from "../../constants/request";
import { cacheVideoUrl, getFileDetail } from "../../services/richmodal";
import { UploadedFile } from "../session/types";

// 定义硬件加速类型
type AccelerationType = "prefer-hardware" | "prefer-software" | undefined;

// 缓存检测结果
let cachedAccelerationMode: AccelerationType | null = null;
let detectionPromise: Promise<AccelerationType> | null = null;

// 视频最大分辨率支持，宽高更大值不超过1920，更小值不超过1080，即1080P
const MAX_VIDEO_RESOLUTION: [number, number] = [1920, 1080];

/**
 * 在页面加载时初始化硬件加速检测
 */
export function initHardwareAccelerationDetection(): void {
  // 确保只在浏览器环境中执行
  if (typeof window !== "undefined") {
    // 页面加载完成后执行检测
    window.addEventListener("load", () => {
      console.log("页面加载完成，开始检测硬件加速支持情况");
      // 触发检测但不等待结果
      detectHardwareAcceleration()
        .then(mode => {
          console.log(`硬件加速检测完成，支持模式: ${mode || "不支持"}`);
        })
        .catch(err => {
          console.error("硬件加速检测失败:", err);
        });
    });
  }
}

// 页面加载完成后自动初始化硬件加速检测
initHardwareAccelerationDetection();

/**
 * 获取硬件加速支持模式
 * @returns 硬件加速支持的模式，如果未检测过则触发检测
 */
export async function getAccelerationMode(): Promise<AccelerationType> {
  // 如果已有缓存结果，直接返回
  if (cachedAccelerationMode !== null) {
    return cachedAccelerationMode;
  }

  // 如果已经有检测正在进行中，复用该Promise
  if (detectionPromise !== null) {
    return detectionPromise;
  }

  // 触发检测并返回Promise
  return detectHardwareAcceleration();
}

/**
 * 检测设备对视频编解码的硬件/软件加速支持情况
 * @returns 返回推荐的加速配置 "prefer-hardware" | "prefer-software" | undefined
 */
export async function detectHardwareAcceleration(): Promise<AccelerationType> {
  // 如果已有缓存结果，直接返回
  if (cachedAccelerationMode !== null) {
    return cachedAccelerationMode;
  }

  // 如果已经有检测正在进行中，复用该Promise
  if (detectionPromise !== null) {
    return detectionPromise;
  }

  // 创建新的检测Promise并缓存
  detectionPromise = _performDetection();

  try {
    // 等待检测完成并缓存结果
    const result = await detectionPromise;
    cachedAccelerationMode = result;
    return result;
  } finally {
    // 检测完成后清除Promise缓存
    detectionPromise = null;
  }
}

/**
 * 执行实际的硬件加速检测逻辑
 * @private 内部使用
 */
async function _performDetection(): Promise<AccelerationType> {
  // 检查VideoEncoder和VideoDecoder API是否可用
  if (
    typeof VideoEncoder === "undefined" ||
    typeof VideoDecoder === "undefined"
  ) {
    console.warn("设备不支持WebCodecs API，无法检测硬件加速");
    return undefined;
  }

  try {
    // 主要测试最常用的编码格式
    const codecs = ["avc1.42E01F"]; // H.264
    const accelerations = ["prefer-hardware", "prefer-software"];

    // 优先检查编码能力（因为编码通常比解码更耗资源）
    let encoderSupport = false;
    let encoderAcceleration: AccelerationType = undefined;

    // 测试编码支持
    for (const acceleration of accelerations) {
      if (encoderSupport) break;

      for (const codec of codecs) {
        const config = {
          codec,
          hardwareAcceleration: acceleration as AccelerationType,
          width: 1280,
          height: 720,
        };

        try {
          // 使用类型断言处理方法不存在的问题
          // @ts-ignore: VideoEncoder.isConfigSupported存在但TypeScript不认识
          const support = await VideoEncoder.isConfigSupported(config);
          if (support.supported) {
            encoderSupport = true;
            encoderAcceleration = acceleration as AccelerationType;
            console.log(`视频编码支持 ${codec} 使用 ${acceleration} 模式`);
            break;
          }
        } catch (e) {
          console.warn(`检测编码支持出错:`, e);
        }
      }
    }

    // 测试解码支持
    let decoderSupport = false;
    let decoderAcceleration: AccelerationType = undefined;

    for (const acceleration of accelerations) {
      if (decoderSupport) break;

      for (const codec of codecs) {
        const config = {
          codec,
          hardwareAcceleration: acceleration as AccelerationType,
          codedWidth: 1280,
          codedHeight: 720,
        };

        try {
          // 使用类型断言处理方法不存在的问题
          // @ts-ignore: VideoDecoder.isConfigSupported存在但TypeScript不认识
          const support = await VideoDecoder.isConfigSupported(config);
          if (support.supported) {
            decoderSupport = true;
            decoderAcceleration = acceleration as AccelerationType;
            console.log(`视频解码支持 ${codec} 使用 ${acceleration} 模式`);
            break;
          }
        } catch (e) {
          console.warn(`检测解码支持出错:`, e);
        }
      }
    }

    // 根据编解码结果，确定最终使用的加速模式
    if (encoderSupport) {
      return encoderAcceleration; // 优先考虑编码能力
    } else if (decoderSupport) {
      return decoderAcceleration; // 如果编码不支持，使用解码能力
    }

    console.log("设备不支持硬件或软件加速");
    return undefined;
  } catch (error) {
    console.error("检测硬件加速支持出错:", error);
    return undefined;
  }
}

/**
 * 获取视频时长
 * @param file 视频文件
 * @returns 视频时长(秒)
 */
export async function getVideoMetaData(file: File) {
  const videoURL = URL.createObjectURL(file);
  const video = document.createElement("video");

  // 创建一个更可靠的 Promise 以获取视频时长
  const getMetaData = () =>
    new Promise<IVideoMetaData>((resolve, reject) => {
      // 设置超时以处理可能的元数据加载问题
      const timeout = setTimeout(() => {
        reject(new Error("获取视频时长超时"));
      }, 10000); // 10秒超时

      // 使用多个事件来确保获取正确的时长
      const checkDuration = () => {
        const { duration, videoHeight = 0, videoWidth = 0 } = video || {};

        console.log(`视频 ${file.name} 高和宽:`, videoHeight, videoWidth);
        URL.revokeObjectURL(videoURL);
        if (duration && duration !== Infinity && duration > 0) {
          clearTimeout(timeout);
          console.log("视频高和宽:", video.height, video.width, video);
          resolve({
            videoWidth,
            videoHeight,
            duration,
          });
        }
      };

      // 监听多个事件以确保获取到正确的时长
      video.onloadedmetadata = checkDuration;
      video.onloadeddata = checkDuration;
      video.ondurationchange = checkDuration;

      video.onerror = () => {
        clearTimeout(timeout);
        reject(new Error("无法获取视频信息"));
      };
    });

  // 预加载视频元数据
  video.preload = "metadata";
  video.src = videoURL;

  const metaData = await getMetaData();
  URL.revokeObjectURL(videoURL); // 释放URL资源
  return metaData;
}

export function getVideoThumbnail(file: File): Promise<[string, File]> {
  return new Promise((r, j) => {
    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.crossOrigin = "anonymous";
    video.currentTime = 1;
    video.muted = true;
    video.play();

    video.onloadeddata = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth / 2;
      canvas.height = video.videoHeight / 2;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/png");

        canvas.toBlob(blob => {
          if (blob) {
            const file = new File([blob], "image.jpeg", { type: blob.type });
            r([dataUrl, file]);
          }
        }, "image/jpeg");
      }

      video.pause();
      URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      j();
    };
  });
}

/**
 * 获取视频URL的时长
 * @param url 视频URL
 * @returns 视频时长(秒)
 */
export function getVideoUrlDuration(url: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      resolve(video.duration);
      video.remove();
    };

    video.onerror = () => {
      reject(new Error("获取视频时长失败"));
      video.remove();
    };

    video.src = url;
  });
}

export function checkResolution(
  height: number,
  width: number,
  max: [number, number],
): boolean {
  const maxDimension = Math.max(width, height);
  const minDimension = Math.min(width, height);
  // 检查分辨率是否符合要求
  const isResolutionValid = !(maxDimension > max[0] || minDimension > max[1]);
  return isResolutionValid;
}

export function filterValidVideos(
  videos: IVideoFileUploadInfo[],
  max: number,
): [IVideoFileUploadInfo[], IUploadCheckResult] {
  let totalDuration = 0;
  const result: IVideoFileUploadInfo[] = [];
  let durationInvalid = false,
    resolutionInvalid = false;
  for (const video of videos) {
    const { duration, videoWidth = 0, videoHeight = 0 } = video?.metaData || {};
    if (duration && totalDuration + duration <= max) {
      // 时长没问题，再进行分辨率的判断
      if (checkResolution(videoHeight, videoWidth, MAX_VIDEO_RESOLUTION)) {
        result.push(video);
        totalDuration += duration;
      } else {
        resolutionInvalid = true;
      }
    } else {
      durationInvalid = true;
      break;
    }
  }

  return [result, { durationInvalid, resolutionInvalid }];
}

/**
 * 视频信息类型(带时长和累计时间)
 */
export interface IProcessedVideoInfo {
  url: string;
  fileId: string;
  duration: number; // 视频时长(毫秒)
  cumulativeTime: number; // 累计时间(毫秒)，表示该视频在拼接后的起始时间
  width?: number;
  height?: number;
}

/**
 * 预处理视频信息，获取每个视频的时长和计算累计时间
 * @param videos 视频信息数组
 * @param progressCallback 进度回调函数
 * @returns 处理后的视频信息数组
 */
export async function preprocessVideos(
  videos: IVideoFileUploadInfo[],
  progressCallback?: (progress: number) => void,
): Promise<IProcessedVideoInfo[]> {
  console.log("开始预处理视频信息");
  progressCallback?.(5);

  const processedVideos: IProcessedVideoInfo[] = [];
  let cumulativeTime = 0;

  // 对每个视频，获取其时长
  for (let i = 0; i < videos.length; i++) {
    const video = videos[i];
    if (!video.url) continue;

    try {
      // 计算视频时长
      const duration = await getVideoUrlDuration(video.url);

      processedVideos.push({
        ...video,
        duration: duration * 1000, // 转换为毫秒
        cumulativeTime: cumulativeTime,
      });

      cumulativeTime += duration * 1000;
      console.log(
        `视频 ${i + 1} 时长: ${duration}秒，累计时间: ${cumulativeTime / 1000}秒`,
      );

      // 更新进度
      if (progressCallback) {
        progressCallback(5 + Math.floor((i / videos.length) * 10));
      }
    } catch (err) {
      console.error(`获取视频 ${i + 1} 时长失败:`, err);
      // 继续处理下一个视频
    }
  }

  console.log("视频预处理完成，共", processedVideos.length, "个有效视频");
  return processedVideos;
}

/**
 * 将剪辑片段映射到对应的视频和视频内的时间位置
 * @param processedVideos 处理后的视频信息
 * @param segments 剪辑片段数组 [[startTime, endTime], ...]
 * @returns 映射后的片段数组 [视频索引, 视频内开始时间(毫秒), 视频内结束时间(毫秒)][]
 */
export function mapSegmentsToVideos(
  processedVideos: IProcessedVideoInfo[],
  segments: number[][],
): [number, number, number][] {
  // 映射后的片段格式: [视频索引, 视频内开始时间(毫秒), 视频内结束时间(毫秒)][]
  const mappedSegments: [number, number, number][] = [];

  segments.forEach(([start, end]) => {
    // 遍历所有视频，找出该时间段覆盖的视频
    for (let i = 0; i < processedVideos.length; i++) {
      const video = processedVideos[i];
      const videoStartTime = video.cumulativeTime;
      const videoEndTime = videoStartTime + video.duration;

      // 检查该片段是否与当前视频有交集
      // 片段开始时间小于视频结束时间 且 片段结束时间大于视频开始时间
      if (start < videoEndTime && end > videoStartTime) {
        // 计算在当前视频中的开始和结束时间
        const segmentStartInVideo = Math.max(0, start - videoStartTime);
        const segmentEndInVideo = Math.min(
          video.duration,
          end - videoStartTime,
        );

        // 只有当片段在视频中有有效长度时才添加
        if (segmentEndInVideo > segmentStartInVideo) {
          mappedSegments.push([i, segmentStartInVideo, segmentEndInVideo]);
        }
      }
    }
  });

  return mappedSegments;
}

/**
 * 安全地分割视频片段
 * @param clip 要分割的MP4Clip
 * @param start 开始时间（毫秒）
 * @param end 结束时间（毫秒）
 * @returns 分割后的MP4Clip，如果分割失败则返回null
 */
export async function performSafeSplit(
  clip: MP4Clip,
  start: number,
  end: number,
): Promise<MP4Clip | null> {
  try {
    // 转换为微秒 (MP4Clip使用微秒作为单位)
    const startMicroseconds = start * 1000;
    const endMicroseconds = end * 1000;

    // 1. 如果起始时间不是0，先切掉前面的部分
    let currentClip = clip;
    if (startMicroseconds > 0) {
      console.log(`分割视频: 去除前 ${startMicroseconds / 1000000}s`);
      try {
        const splitResult = await clip.split(startMicroseconds);
        if (splitResult.length > 1) {
          currentClip = splitResult[1]; // 使用后半部分
          console.log(
            `第一次分割成功，剩余时长: ${currentClip.meta.duration / 1000000}s`,
          );
        } else {
          console.warn("视频前半部分分割失败，使用原视频");
          return null;
        }
      } catch (err) {
        console.error("前半部分分割失败:", err);
        return null;
      }
    }

    // 2. 计算当前片段的时长
    const duration = endMicroseconds - startMicroseconds;

    // 3. 如果需要的片段比当前片段短，再切掉后面的部分
    if (duration < currentClip.meta.duration) {
      console.log(`分割视频: 限制时长为 ${duration / 1000000}s`);
      try {
        const splitResult = await currentClip.split(duration);
        if (splitResult.length > 0) {
          currentClip = splitResult[0]; // 使用前半部分
          console.log(
            `第二次分割成功，最终时长: ${currentClip.meta.duration / 1000000}s`,
          );
        } else {
          console.warn("视频后半部分分割失败，使用当前片段");
        }
      } catch (err) {
        console.error("后半部分分割失败:", err);
        // 返回当前已经处理的片段，而不是完全失败
      }
    }

    return currentClip;
  } catch (err) {
    console.error("视频分割过程出错:", err);
    return null;
  }
}

/**
 * 使用AVCanvas剪辑并拼接多个视频
 * @param processedVideos 处理后的视频信息
 * @param mappedSegments 映射后的片段
 * @param progressCallback 进度回调函数
 * @returns 剪辑后的视频Blob
 */
export async function clipVideosWithAVCanvas(
  processedVideos: IProcessedVideoInfo[],
  mappedSegments: [number, number, number][],
  progressCallback?: (progress: number) => void,
): Promise<Blob> {
  console.log("使用AVCanvas开始多视频剪辑和拼接");
  if (
    typeof VideoEncoder === "undefined" ||
    typeof VideoDecoder === "undefined"
  ) {
    console.log("设备不支持WebCodecs API，无法检测硬件加速");
    alert(
      "设备不支持WebCodecs API，无法进行视频剪辑，请切换浏览器为chrome或Edge94版本以上，或者使用浏览器极速模式",
    );
    return new Blob();
  }
  progressCallback?.(20);

  try {
    // 获取第一个视频的尺寸信息（或使用默认值）
    let targetWidth = 1280;
    let targetHeight = 720;

    // 获取硬件加速支持情况（使用缓存的结果）
    const accelerationMode = await getAccelerationMode();
    console.log(`使用加速模式: ${accelerationMode || "不使用加速"}`);

    try {
      // 尝试获取第一个视频的尺寸
      const firstVideo = processedVideos[0];
      const response = await fetch(firstVideo.url);
      if (!response.ok) {
        throw new Error(`获取视频失败: ${response.status}`);
      }

      const videoArrayBuffer = await response.arrayBuffer();
      const tempVideoStream = new Response(videoArrayBuffer.slice(0)).body!;
      const tempVideoClip = new MP4Clip(tempVideoStream, {
        __unsafe_hardwareAcceleration__: accelerationMode,
      });

      // 等待视频元数据准备好
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("视频元数据准备超时"));
        }, 30000);

        const checkReady = () => {
          if (tempVideoClip.meta && tempVideoClip.meta.duration > 0) {
            clearTimeout(timeout);
            resolve();
          } else if ((tempVideoClip as any).errMsg) {
            clearTimeout(timeout);
            reject(
              new Error(`视频元数据获取错误: ${(tempVideoClip as any).errMsg}`),
            );
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      });

      targetWidth = tempVideoClip.meta.width || targetWidth;
      targetHeight = tempVideoClip.meta.height || targetHeight;

      // 确保宽高为偶数（H264编码要求）
      targetWidth = targetWidth % 2 === 0 ? targetWidth : targetWidth - 1;
      targetHeight = targetHeight % 2 === 0 ? targetHeight : targetHeight - 1;

      console.log(`目标视频分辨率: ${targetWidth}x${targetHeight}`);
    } catch (err) {
      console.warn("获取视频尺寸失败，使用默认尺寸:", err);
    }

    // 创建一个新的 AVCanvas
    const outputCanvas = new AVCanvas(document.createElement("div"), {
      bgColor: "#000",
      width: targetWidth,
      height: targetHeight,
    });

    // 处理所有映射的片段
    let processedCount = 0;
    let currentOutputOffset = 0; // 在输出视频中的当前偏移量
    const totalSegments = mappedSegments.length;

    // 串行处理每个片段
    for (let i = 0; i < mappedSegments.length; i++) {
      const [videoIndex, startInVideo, endInVideo] = mappedSegments[i];
      const videoInfo = processedVideos[videoIndex];

      try {
        console.log(
          `处理片段 ${i + 1}/${totalSegments}: 视频${videoIndex + 1} [${startInVideo / 1000}s, ${endInVideo / 1000}s]`,
        );

        // 更新进度
        if (progressCallback) {
          progressCallback(20 + Math.floor((i / totalSegments) * 60));
        }

        // 获取视频流
        const response = await fetch(videoInfo.url);
        if (!response.ok) {
          throw new Error(`获取视频失败: ${response.status}`);
        }

        // 获取视频数据
        const videoArrayBuffer = await response.arrayBuffer();
        const videoStream = new Response(videoArrayBuffer.slice(0)).body!;

        // 创建新的MP4Clip对象
        const videoClip = new MP4Clip(videoStream, {
          __unsafe_hardwareAcceleration__: accelerationMode,
        });

        // 等待视频准备就绪
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("视频准备超时"));
          }, 30000);

          const checkReady = () => {
            if (videoClip.meta && videoClip.meta.duration > 0) {
              clearTimeout(timeout);
              resolve();
            } else if ((videoClip as any).errMsg) {
              clearTimeout(timeout);
              reject(new Error(`视频加载错误: ${(videoClip as any).errMsg}`));
            } else {
              setTimeout(checkReady, 100);
            }
          };
          checkReady();
        });

        // 分割视频获取指定时间段
        const targetClip = await performSafeSplit(
          videoClip,
          startInVideo,
          endInVideo,
        );

        if (!targetClip) {
          console.warn(
            `处理视频 ${videoIndex + 1} 片段 [${startInVideo / 1000}s-${endInVideo / 1000}s] 失败，跳过`,
          );
          continue;
        }

        // 创建精灵并设置偏移
        const segmentSprite = new VisibleSprite(targetClip);
        segmentSprite.time.offset = currentOutputOffset;

        // 确保精灵尺寸符合要求
        try {
          // @ts-ignore
          if (segmentSprite?.setSize) {
            // @ts-ignore
            segmentSprite.setSize(targetWidth, targetHeight);
            // @ts-ignore
          } else if (segmentSprite?.resize) {
            // @ts-ignore
            segmentSprite.resize(targetWidth, targetHeight);
            // @ts-ignore
          } else if (segmentSprite?.size) {
            // @ts-ignore
            segmentSprite.size.width = targetWidth;
            // @ts-ignore
            segmentSprite.size.height = targetHeight;
          }
        } catch (sizeError) {
          console.warn("设置精灵尺寸失败，使用默认尺寸:", sizeError);
        }

        // 添加到输出画布
        await outputCanvas.addSprite(segmentSprite);
        processedCount++;

        // 更新下一个片段的偏移量
        currentOutputOffset += targetClip.meta.duration;

        console.log(
          `片段 ${i + 1} 添加完成，当前输出视频长度: ${currentOutputOffset / 1000000}s`,
        );
      } catch (err) {
        console.error(`处理视频 ${videoIndex + 1} 片段失败:`, err);
      }
    }

    if (processedCount === 0) {
      throw new Error("没有成功创建任何视频片段");
    }

    console.log(`所有片段处理完成，开始导出...`);
    progressCallback?.(80);

    // 导出合成后的视频
    const combinator = await outputCanvas.createCombinator({
      __unsafe_hardwareAcceleration__: accelerationMode,
      width: targetWidth,
      height: targetHeight,
    });

    // 创建可用于播放的blob
    const chunks: unknown[] = [];
    const writer = new WritableStream({
      write(chunk) {
        chunks.push(chunk);
      },
      close() {
        console.log("视频生成完成");
        progressCallback?.(100);
      },
    });

    // 开始导出视频流
    await combinator.output().pipeTo(writer);
    // 创建从chunks创建Blob
    const finalBlob = new Blob(chunks as BlobPart[], { type: "video/mp4" });

    // 清理资源
    setTimeout(() => {
      outputCanvas.destroy();
    }, 1000);

    return finalBlob;
  } catch (error) {
    console.error("多视频剪辑处理错误:", error);
    throw error;
  }
}

/**
 * 多视频剪辑方法 - 将多个视频片段剪辑并拼接成一个完整视频
 * @param videos 视频信息数组
 * @param segments 剪辑片段数组 [[videoIndex, startTime, endTime], ...] 或 [[startTime, endTime], ...]
 * @param progressCallback 进度回调函数
 * @returns 剪辑后的视频Blob
 */
export async function clipMultipleVideos(
  videos: IVideoFileUploadInfo[],
  segments: number[][] | [number, number, number][],
  progressCallback?: (progress: number) => void,
): Promise<Blob> {
  console.log(
    "开始多视频剪辑，视频数量:",
    videos.length,
    "片段数量:",
    segments.length,
  );

  try {
    // 检查片段格式: 如果第一个元素是三个数字的数组，则认为是带索引的格式
    const isIndexedFormat = segments.length > 0 && segments[0].length === 3;

    if (isIndexedFormat) {
      // 新格式: [[videoIndex, startTime, endTime], ...]
      console.log("使用带索引的片段格式处理多视频");

      // 直接使用带索引的片段进行处理
      const mappedSegments = segments.map(segment => {
        // 确保segment是三元素数组
        if (segment.length !== 3) {
          throw new Error("不支持的片段格式");
        }

        const videoIndex = segment[0];
        const start = segment[1];
        const end = segment[2];

        // 验证索引是否有效
        if (videoIndex < 0 || videoIndex >= videos.length) {
          throw new Error(
            `视频索引 ${videoIndex} 超出范围 [0-${videos.length - 1}]`,
          );
        }

        return [videoIndex, start, end] as [number, number, number];
      });

      console.log("按索引映射的片段:", mappedSegments);

      // 处理每个视频的片段并拼接
      return await clipVideosWithIndices(
        videos,
        mappedSegments,
        progressCallback,
      );
    } else {
      // 旧格式: [[startTime, endTime], ...]
      // 1. 预处理视频信息，计算每个视频的时长和累计时间
      const processedVideos = await preprocessVideos(videos, progressCallback);

      if (!processedVideos.length) {
        throw new Error("无法获取视频信息");
      }

      // 2. 将时间段映射到对应的视频和视频内的时间
      const mappedSegments = mapSegmentsToVideos(
        processedVideos,
        segments as number[][],
      );
      console.log("映射后的片段:", mappedSegments);

      // 3. 使用基于AVCanvas的方法进行多视频拼接
      return await clipVideosWithAVCanvas(
        processedVideos,
        mappedSegments,
        progressCallback,
      );
    }
  } catch (error) {
    console.error("多视频剪辑失败:", error);
    throw error;
  }
}

/**
 * 按索引剪辑多个视频 - 直接使用带视频索引的片段
 * @param videos 视频信息数组
 * @param indexedSegments 带索引的片段数组 [[videoIndex, startTime, endTime], ...]
 * @param progressCallback 进度回调函数
 * @returns 剪辑后的视频Blob
 */
export async function clipVideosWithIndices(
  videos: IVideoFileUploadInfo[],
  indexedSegments: [number, number, number][],
  progressCallback?: (progress: number) => void,
): Promise<Blob> {
  console.log("使用带索引的方法处理多视频");
  progressCallback?.(10);

  try {
    // 获取硬件加速支持情况（使用缓存的结果）
    const accelerationMode = await getAccelerationMode();
    console.log(`使用加速模式: ${accelerationMode || "不使用加速"}`);

    // 获取第一个视频的尺寸信息（或使用默认值）
    let targetWidth = 1280;
    let targetHeight = 720;

    try {
      // 尝试获取第一个有效视频的尺寸
      let firstVideoIndex = 0;
      if (indexedSegments.length > 0) {
        firstVideoIndex = indexedSegments[0][0];
      }

      const firstVideo = videos[firstVideoIndex];
      const response = await fetch(firstVideo.url);
      if (!response.ok) {
        throw new Error(`获取视频失败: ${response.status}`);
      }

      const videoArrayBuffer = await response.arrayBuffer();
      const tempVideoStream = new Response(videoArrayBuffer.slice(0)).body!;
      const tempVideoClip = new MP4Clip(tempVideoStream, {
        __unsafe_hardwareAcceleration__: accelerationMode,
      });

      // 等待视频元数据准备好
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("视频元数据准备超时"));
        }, 30000);

        const checkReady = () => {
          if (tempVideoClip.meta && tempVideoClip.meta.duration > 0) {
            clearTimeout(timeout);
            resolve();
          } else if ((tempVideoClip as any).errMsg) {
            clearTimeout(timeout);
            reject(
              new Error(`视频元数据获取错误: ${(tempVideoClip as any).errMsg}`),
            );
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      });

      targetWidth = tempVideoClip.meta.width || targetWidth;
      targetHeight = tempVideoClip.meta.height || targetHeight;

      // 确保宽高为偶数（H264编码要求）
      targetWidth = targetWidth % 2 === 0 ? targetWidth : targetWidth - 1;
      targetHeight = targetHeight % 2 === 0 ? targetHeight : targetHeight - 1;

      console.log(`目标视频分辨率: ${targetWidth}x${targetHeight}`);
    } catch (err) {
      console.warn("获取视频尺寸失败，使用默认尺寸:", err);
    }

    // 创建一个新的 AVCanvas
    const outputCanvas = new AVCanvas(document.createElement("div"), {
      bgColor: "#000",
      width: targetWidth,
      height: targetHeight,
    });

    let processedCount = 0;
    let currentOutputOffset = 0; // 在输出视频中的当前偏移量
    const totalSegments = indexedSegments.length;

    // 性能优化: 按视频索引分组片段
    const segmentsByVideoIndex: Record<number, [number, number, number][]> = {};

    // 对片段进行分组，相同视频索引的片段放在一起
    indexedSegments.forEach(segment => {
      const [videoIndex, startTime, endTime] = segment;
      if (!segmentsByVideoIndex[videoIndex]) {
        segmentsByVideoIndex[videoIndex] = [];
      }
      segmentsByVideoIndex[videoIndex].push([videoIndex, startTime, endTime]);
    });

    console.log("按视频索引分组的片段:", segmentsByVideoIndex);

    // 为每个视频创建一个缓存对象
    type VideoCache = {
      arrayBuffer: ArrayBuffer;
      clips: Record<string, MP4Clip>; // 缓存已处理的片段，键为"start_end"
    };

    const videoCache: Record<number, VideoCache> = {};

    // 第一步: 预加载所有需要的视频
    progressCallback?.(15);
    console.log("开始预加载视频...");

    await Promise.all(
      Object.keys(segmentsByVideoIndex).map(async videoIndexStr => {
        const videoIndex = parseInt(videoIndexStr);
        const videoInfo = videos[videoIndex];

        try {
          // 获取视频流
          console.log(`预加载视频 ${videoIndex + 1} 的数据`);
          const response = await fetch(videoInfo.url);
          if (!response.ok) {
            throw new Error(`获取视频失败: ${response.status}`);
          }

          // 获取并缓存视频数据
          const videoArrayBuffer = await response.arrayBuffer();

          // 将视频数据缓存起来
          videoCache[videoIndex] = {
            arrayBuffer: videoArrayBuffer,
            clips: {},
          };

          console.log(`视频 ${videoIndex + 1} 预加载完成`);
        } catch (err) {
          console.error(`预加载视频 ${videoIndex + 1} 失败:`, err);
          // 预加载失败不影响后续处理，但会在后续步骤中重试
        }
      }),
    );

    progressCallback?.(20);
    console.log("视频预加载完成");

    // 第二步: 并行处理所有片段，生成精灵
    console.log("开始并行处理片段...");

    // 存储所有片段处理的Promise和对应的索引
    type SegmentResult = {
      index: number; // 原始片段在indexedSegments中的索引
      sprite: VisibleSprite;
      duration: number;
    };

    // 处理单个片段的函数 - 使用函数表达式而不是函数声明
    const processSegment = async (
      segment: [number, number, number],
      originalIndex: number,
    ): Promise<SegmentResult | null> => {
      const [videoIndex, startInVideo, endInVideo] = segment;
      const videoInfo = videos[videoIndex];

      try {
        console.log(
          `处理片段 ${originalIndex + 1}/${totalSegments}: 视频${videoIndex + 1} [${startInVideo / 1000}s, ${endInVideo / 1000}s]`,
        );

        // 尝试从缓存获取视频数据
        let videoArrayBuffer: ArrayBuffer;

        if (videoCache[videoIndex]?.arrayBuffer) {
          // 使用缓存的视频数据
          videoArrayBuffer = videoCache[videoIndex].arrayBuffer;
          console.log(`使用缓存的视频 ${videoIndex + 1} 数据`);
        } else {
          // 如果预加载失败，重新获取视频数据
          console.log(`预加载失败，重新获取视频 ${videoIndex + 1} 数据`);
          const response = await fetch(videoInfo.url);
          if (!response.ok) {
            throw new Error(`获取视频失败: ${response.status}`);
          }
          videoArrayBuffer = await response.arrayBuffer();

          // 更新缓存
          if (!videoCache[videoIndex]) {
            videoCache[videoIndex] = {
              arrayBuffer: videoArrayBuffer,
              clips: {},
            };
          } else {
            videoCache[videoIndex].arrayBuffer = videoArrayBuffer;
          }
        }

        // 为当前片段生成唯一键
        const clipKey = `${startInVideo}_${endInVideo}`;

        // 尝试从缓存获取已处理的片段
        let targetClip: MP4Clip | null = null;

        if (videoCache[videoIndex]?.clips[clipKey]) {
          // 使用缓存的片段
          targetClip = videoCache[videoIndex].clips[clipKey];
          console.log(
            `使用缓存的片段 [${startInVideo / 1000}s-${endInVideo / 1000}s]`,
          );
        } else {
          // 创建新的视频流和MP4Clip
          const videoStream = new Response(videoArrayBuffer.slice(0)).body!;

          const videoClip = new MP4Clip(videoStream, {
            __unsafe_hardwareAcceleration__: accelerationMode,
          });

          // 等待视频准备就绪
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("视频准备超时"));
            }, 30000);

            const checkReady = () => {
              if (videoClip.meta && videoClip.meta.duration > 0) {
                clearTimeout(timeout);
                resolve();
              } else if ((videoClip as any).errMsg) {
                clearTimeout(timeout);
                reject(new Error(`视频加载错误: ${(videoClip as any).errMsg}`));
              } else {
                setTimeout(checkReady, 100);
              }
            };
            checkReady();
          });

          // 分割视频获取指定时间段
          targetClip = await performSafeSplit(
            videoClip,
            startInVideo,
            endInVideo,
          );

          // 缓存处理后的片段
          if (targetClip) {
            videoCache[videoIndex].clips[clipKey] = targetClip;
          }
        }

        if (!targetClip) {
          console.warn(
            `处理视频 ${videoIndex + 1} 片段 [${startInVideo / 1000}s-${endInVideo / 1000}s] 失败，跳过`,
          );
          return null;
        }

        // 创建精灵，但不立即添加到画布
        const segmentSprite = new VisibleSprite(targetClip);

        // 精灵尺寸设置放到后面统一处理

        return {
          index: originalIndex,
          sprite: segmentSprite,
          duration: targetClip.meta.duration,
        };
      } catch (err) {
        console.error(`处理视频 ${videoIndex + 1} 片段失败:`, err);
        return null;
      }
    };

    // 等待所有片段处理完成
    const results = await Promise.all(
      indexedSegments.map((segment, originalIndex) => {
        return processSegment(segment, originalIndex);
      }),
    );
    progressCallback?.(60);

    // 过滤出成功处理的片段，并按原始顺序排序
    const validResults = results.filter(
      result => result !== null,
    ) as SegmentResult[];

    // 如果没有成功处理的片段，抛出错误
    if (validResults.length === 0) {
      throw new Error("没有成功创建任何视频片段");
    }

    console.log(`成功处理 ${validResults.length}/${totalSegments} 个片段`);

    // 第三步: 按原始顺序将精灵添加到画布
    // 重要：虽然前面是并行处理的，但添加到画布必须按顺序进行
    console.log("开始按顺序添加片段到画布...");

    // 按照原始顺序对结果进行排序
    validResults.sort((a, b) => a.index - b.index);

    // 按顺序将精灵添加到画布
    for (let i = 0; i < validResults.length; i++) {
      const result = validResults[i];
      const segmentSprite = result.sprite;

      // 设置偏移
      segmentSprite.time.offset = currentOutputOffset;

      // 确保精灵尺寸符合要求
      try {
        // @ts-ignore
        if (segmentSprite?.setSize) {
          // @ts-ignore
          segmentSprite.setSize(targetWidth, targetHeight);
          // @ts-ignore
        } else if (segmentSprite?.resize) {
          // @ts-ignore
          segmentSprite.resize(targetWidth, targetHeight);
          // @ts-ignore
        } else if (segmentSprite?.size) {
          // @ts-ignore
          segmentSprite.size.width = targetWidth;
          // @ts-ignore
          segmentSprite.size.height = targetHeight;
        }
      } catch (sizeError) {
        console.warn("设置精灵尺寸失败，使用默认尺寸:", sizeError);
      }

      // 添加到输出画布
      await outputCanvas.addSprite(segmentSprite);
      processedCount++;

      // 更新下一个片段的偏移量
      currentOutputOffset += result.duration;

      console.log(
        `添加片段 ${i + 1}/${validResults.length} 完成，当前输出视频长度: ${currentOutputOffset / 1000000}s`,
      );

      // 更新进度
      progressCallback?.(60 + Math.floor((i / validResults.length) * 20));
    }

    if (processedCount === 0) {
      throw new Error("没有成功添加任何视频片段到画布");
    }

    console.log(`所有片段处理完成，开始导出...`);
    progressCallback?.(80);

    // 导出合成后的视频
    const combinator = await outputCanvas.createCombinator({
      __unsafe_hardwareAcceleration__: accelerationMode,
      width: targetWidth,
      height: targetHeight,
    });

    // 创建可用于播放的blob
    const chunks: unknown[] = [];
    const writer = new WritableStream({
      write(chunk) {
        chunks.push(chunk);
      },
      close() {
        console.log("视频生成完成");
        progressCallback?.(100);
      },
    });

    // 开始导出视频流
    await combinator.output().pipeTo(writer);
    // 创建从chunks创建Blob
    const finalBlob = new Blob(chunks as BlobPart[], { type: "video/mp4" });

    // 清理资源
    setTimeout(() => {
      outputCanvas.destroy();

      // 清理缓存
      Object.keys(videoCache).forEach(key => {
        const index = parseInt(key);
        if (videoCache[index]) {
          Object.keys(videoCache[index].clips).forEach(clipKey => {
            try {
              videoCache[index].clips[clipKey].destroy?.();
            } catch (e) {
              // 忽略销毁错误
            }
          });
          videoCache[index].clips = {};
        }
      });
    }, 1000);

    return finalBlob;
  } catch (error) {
    console.error("多视频剪辑处理错误:", error);
    throw error;
  }
}

/**
 * 上传剪辑后的视频到OSS并缓存URL
 * @param videoBlob 剪辑后的视频Blob
 * @param cacheKey 缓存键
 * @returns 上传成功后的视频信息，失败则返回null
 */
export async function uploadClippedVideoAndCache(
  videoBlob: Blob,
  cacheKey: string,
): Promise<UploadedFile | null> {
  try {
    // 创建File对象
    const fileName = `clipped_video_${new Date().getTime()}.mp4`;
    const clippedVideoFile = new File([videoBlob], fileName, {
      type: "video/mp4",
    });

    console.log("开始上传剪辑后的视频到OSS...");

    // 上传视频文件
    const fileInfo = await uploadAndCommitCache(clippedVideoFile, cacheKey);
    if (!fileInfo || fileInfo?.status !== "success") {
      console.error("视频上传失败");
      return null;
    }
    console.log("视频上传成功，fileId:", fileInfo.file_id);

    return fileInfo;
  } catch (error) {
    console.error("上传视频并缓存URL失败:", error);
    return null;
  }
}

/**
 * 从视频URL生成缩略图
 * @param videoUrl 视频URL
 * @param timeInSeconds 要截取的时间点（秒）
 * @param quality 缩略图质量（0-1）
 * @returns Promise<string> 缩略图的Data URL
 */
export async function generateThumbnailFromVideo(
  videoUrl: string,
  timeInSeconds = 0.5,
  quality = 0.6,
): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      // 创建视频元素
      const video = document.createElement("video");
      video.crossOrigin = "anonymous"; // 尝试解决CORS问题
      video.muted = true;
      video.playsInline = true;

      // 监听视频可以寻址时生成缩略图
      video.addEventListener("canplay", () => {
        try {
          // 设置到指定时间点
          video.currentTime = Math.min(timeInSeconds, video.duration - 0.1);
        } catch (err) {
          console.error("设置视频时间失败:", err);
          // 如果设置时间失败，继续使用默认时间
        }
      });

      // 监听视频寻址完成，此时可以截取画面
      video.addEventListener("seeked", () => {
        try {
          // 创建canvas来绘制视频帧
          const canvas = document.createElement("canvas");
          // 设置canvas尺寸，可以调整缩略图大小
          const width = 240; // 缩略图宽度
          const height = Math.floor(
            width * (video.videoHeight / video.videoWidth),
          );

          canvas.width = width;
          canvas.height = height;

          // 绘制视频帧到canvas
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("无法创建canvas上下文"));
            return;
          }

          ctx.drawImage(video, 0, 0, width, height);

          // 转换为dataURL
          const dataUrl = canvas.toDataURL("image/jpeg", quality);

          // 清理资源
          video.pause();
          video.src = "";
          video.load();

          resolve(dataUrl);
        } catch (err) {
          console.error("生成缩略图失败:", err);
          reject(err);
        }
      });

      // 监听错误
      video.addEventListener("error", err => {
        console.error("视频加载错误:", err);
        reject(new Error("视频加载失败"));
      });

      // 设置视频源并开始加载
      video.src = videoUrl;
      video.load();

      // 设置超时，避免无限等待
      setTimeout(() => {
        reject(new Error("生成缩略图超时"));
      }, 15000);
    } catch (err) {
      console.error("生成缩略图过程出错:", err);
      reject(err);
    }
  });
}

/**
 * 快速从视频URL获取首帧图像
 * @param videoUrl 视频URL
 * @param quality 图像质量（0-1）
 * @param maxWidth 最大宽度（可选）
 * @returns Promise<string> 首帧图像的Data URL
 */
export async function getVideoFirstFrame(
  videoUrl: string,
  quality = 0.8,
  maxWidth?: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "metadata";
    // 设置超时
    const timeout = setTimeout(() => {
      reject(new Error("获取视频首帧超时"));
    }, 8000);
    // 使用loadeddata事件，这是最早可以获取到视频帧的事件
    video.addEventListener("loadeddata", () => {
      try {
        // 确保视频已经准备好第一帧
        if (video.readyState < 2) {
          // 如果视频还没准备好，等待一点时间再尝试
          setTimeout(() => {
            captureFrame();
          }, 100);
        } else {
          captureFrame();
        }
      } catch (err) {
        clearTimeout(timeout);
        reject(err);
      }
    });
    // 错误处理
    video.addEventListener("error", () => {
      clearTimeout(timeout);
      reject(new Error("视频加载失败"));
    });
    // 捕获视频帧的函数
    const captureFrame = () => {
      try {
        // 创建canvas
        const canvas = document.createElement("canvas");
        // 计算尺寸
        let width = video.videoWidth;
        let height = video.videoHeight;
        // 如果指定了最大宽度且视频宽度大于最大宽度，则按比例缩小
        if (maxWidth && width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = Math.floor(height * ratio);
        }
        // 设置canvas尺寸
        canvas.width = width;
        canvas.height = height;
        // 绘制视频帧
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("无法创建canvas上下文");
        }
        // 绘制视频的第一帧
        ctx.drawImage(video, 0, 0, width, height);
        // 转换为dataURL
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        // 清理资源
        clearTimeout(timeout);
        video.pause();
        video.src = "";
        video.load();
        resolve(dataUrl);
      } catch (err) {
        clearTimeout(timeout);
        console.error("捕获视频首帧失败:", err);
        reject(err);
      }
    };
    // 设置视频源并开始加载
    // 使用currentTime=0确保获取第一帧
    video.currentTime = 0;
    video.src = videoUrl;
    video.load();
  });
}
